import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchShopCategories,
} from '@features/products/services/categoryCatalogService';
import {
  Category,
  SubCategory,
} from '@features/products/graphql/types';
import { checkingImageUrl } from '@shared/utils/imageUrlCheck';
import { getFirstShopId, MULTI_SHOP_ENABLED } from '@core/config/shops';
import { getImageBaseForTenantShop } from '@core/config/shops';
import { useApolloClients } from '@core/context/ApolloClientsContext';
import {
  readCategoriesCache,
  writeCategoriesCache,
} from '@core/cache/queryCache';
import { scopeCategoryId } from '@core/utils/scopedCategory';
import { parseGlobalProductId } from '@core/utils/globalIds';
import {
  mergeCatalogCategoriesByName,
  mergeCategoriesByDisplayName,
  type ShopCategoryRow,
} from '@core/utils/mergeTenantCategories';
import { useShopIdsKey, parseShopIdsKey } from '@core/tenant/useShopIdsKey';
import type { TenantShop } from '@core/tenant/types';

function decorateCategory(
  category: Category,
  lineShopId: string | undefined,
  allShops: TenantShop[]
): Category {
  const base = getImageBaseForTenantShop(lineShopId, allShops);
  return {
    ...category,
    image: checkingImageUrl(category.image, base),
    mst_sub_category: (() => {
      const subData = category.mst_sub_category;
      const subArray = (
        Array.isArray(subData) ? subData : subData ? [subData] : []
      ) as SubCategory[];
      return subArray.map(sub => ({
        ...sub,
        id:
          sub.id && lineShopId ? scopeCategoryId(lineShopId, sub.id) : sub.id,
        image: checkingImageUrl(sub.image, base),
      }));
    })(),
  };
}

export interface CategoriesContextValue {
  categories: Category[];
  loading: boolean;
  error: Error | null;
  homeTabs: Category[];
  homeTabSources: Record<string, { shopId: string; localCategoryId: string }[]>;
}

const CategoriesContext = createContext<CategoriesContextValue | null>(null);

export function CategoriesProvider({ children }: { children: ReactNode }) {
  const { shops, tenantLoading, getClient } = useApolloClients();
  const shopIdsKey = useShopIdsKey();
  const shopIds = useMemo(() => parseShopIdsKey(shopIdsKey), [shopIdsKey]);
  const defaultShopId = getFirstShopId(shops);

  const [rawDecorated, setRawDecorated] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadCategories = useCallback(async () => {
    if (tenantLoading) return;

    const ids = MULTI_SHOP_ENABLED
      ? shopIds
      : shopIds.length
        ? [shopIds[0]]
        : [defaultShopId];

    if (!ids.length) {
      setRawDecorated([]);
      setLoading(false);
      return;
    }

    const cached =
      MULTI_SHOP_ENABLED ? readCategoriesCache<Category[]>(ids) : null;
    if (cached?.length) {
      setRawDecorated(cached);
    }
    setLoading(!cached?.length);
    setError(null);
    try {
      const settled = await Promise.allSettled(
        ids.map(shopId =>
          fetchShopCategories(getClient(shopId), shopId, 'network-only')
        )
      );

      const merged: Category[] = [];
      settled.forEach((result, i) => {
        const sid = ids[i]!;
        if (result.status === 'rejected') {
          console.warn(`[categories] shop "${sid}" failed:`, result.reason);
          return;
        }
        for (const c of result.value) {
          if (!c.id) continue;
          merged.push(
            decorateCategory(
              {
                ...c,
                id: MULTI_SHOP_ENABLED ? scopeCategoryId(sid, c.id) : c.id,
              },
              MULTI_SHOP_ENABLED ? sid : defaultShopId,
              shops
            )
          );
        }
      });

      setRawDecorated(merged);
      if (MULTI_SHOP_ENABLED) {
        writeCategoriesCache(ids, merged);
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setRawDecorated([]);
    } finally {
      setLoading(false);
    }
  }, [tenantLoading, shopIdsKey, shopIds, defaultShopId, shops, getClient]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const singleCategories = MULTI_SHOP_ENABLED ? [] : rawDecorated;
  const decorated = rawDecorated;

  const categories = useMemo(
    () => mergeCatalogCategoriesByName(decorated),
    [decorated]
  );

  const homeTabs = useMemo(() => {
    if (!MULTI_SHOP_ENABLED) return singleCategories;
    const rows: ShopCategoryRow[] = [];
    for (const c of rawDecorated) {
      if (!c.id || !c.category?.trim()) continue;
      const parsed = parseGlobalProductId(c.id);
      if (!parsed) continue;
      rows.push({
        shopId: parsed.shopId,
        localCategoryId: parsed.localProductId,
        category: { ...c, id: parsed.localProductId },
      });
    }
    return mergeCategoriesByDisplayName(rows, shops).tabs;
  }, [MULTI_SHOP_ENABLED, rawDecorated, singleCategories, shops]);

  const homeTabSources = useMemo(() => {
    if (!MULTI_SHOP_ENABLED) {
      return {} as CategoriesContextValue['homeTabSources'];
    }
    const rows: ShopCategoryRow[] = [];
    for (const c of rawDecorated) {
      if (!c.id || !c.category?.trim()) continue;
      const parsed = parseGlobalProductId(c.id);
      if (!parsed) continue;
      rows.push({
        shopId: parsed.shopId,
        localCategoryId: parsed.localProductId,
        category: { ...c, id: parsed.localProductId },
      });
    }
    return mergeCategoriesByDisplayName(rows, shops).sourcesByTabId;
  }, [MULTI_SHOP_ENABLED, rawDecorated, shops]);

  const value = useMemo(
    (): CategoriesContextValue => ({
      categories,
      loading,
      error,
      homeTabs,
      homeTabSources,
    }),
    [categories, loading, error, homeTabs, homeTabSources]
  );

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategoriesContext(): CategoriesContextValue {
  const ctx = useContext(CategoriesContext);
  if (!ctx) {
    throw new Error('useCategoriesContext must be used within CategoriesProvider');
  }
  return ctx;
}
